import { DocumentMapping } from "@/configuration/document-mappings/types";
import { Classification } from "@/src/models/constants/classifications";

export type CvvcDocumentMapping = DocumentMapping & { assignmentStrategy?: "provider_staff_bucket" };

// CVVC's document taxonomy mirrors StrideCare's near-exactly (same Athena practice, same document
// type IDs) - see the index-mapping sheet under source/.
export const CVVC_MAPPINGS: Partial<Record<Classification, CvvcDocumentMapping>> = {
  [Classification.CLEARANCE_REQUEST_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: "provider_staff_bucket",
    displayName: "Cardiac clearances",
    documentLabel: "Cardiac Clearance",
    documentTypeId: 309005,
    folder: "Clinical Document",
    folderMapping: "Clinical Document",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.CLEARANCE_REQUEST_RESPONSE,
    group: "stridecare-cvvc",
  },
  [Classification.DME_ORDER]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: "provider_staff_bucket",
    displayName: "DME Order to be signed",
    documentLabel: "Care Plan Oversight",
    documentTypeId: 394714,
    folder: "Physician Authorization",
    folderMapping: "Physician Authorization",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.DME_ORDER,
    group: "stridecare-cvvc",
  },
  [Classification.ADMIN_DISABILITY_FORMS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: "provider_staff_bucket",
    displayName: "Disability/FMLA forms",
    documentLabel: "Request for Additional Information",
    documentTypeId: 390522,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.ADMIN_DISABILITY_FORMS,
    group: "stridecare-cvvc",
  },
  // Sheet cell was mangled (embedded newlines); read as "Upload normally; if no patient match in
  // Athena, email Tarynne Banner (Tarynne.Banner@stridecare.com) instead of the default wait-then-
  // upload behavior." The email-fallback branch is workflow-steps.ts logic (phase 6), not a
  // document-mappings field - confirmed with Jack, not yet implemented.
  [Classification.FINANCIAL_BILLING_RECORDS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Financial records",
    documentLabel: "Valerie Health - Financial Document",
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.FINANCIAL_BILLING_RECORDS,
    group: "stridecare-cvvc",
  },
  [Classification.IMAGING_RESULTS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: "provider_staff_bucket",
    displayName: "Imaging results",
    documentLabel: "{{ imagingType }}",
    folder: "Imaging/Diagnostic Result",
    folderMapping: "Imaging/Diagnostic Result",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.IMAGING_RESULTS,
    group: "stridecare-cvvc",
  },
  [Classification.LAB_RESULTS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: "provider_staff_bucket",
    displayName: "Lab results",
    documentLabel: "Lab*",
    documentTypeId: 400537,
    folder: "Lab Results",
    folderMapping: "Lab Results",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.LAB_RESULTS,
    group: "stridecare-cvvc",
  },
  [Classification.LEGAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["Close Document"],
    assignedTo: null,
    displayName: "Power of attorney documents",
    documentLabel: "Health Care Power of Attorney",
    documentTypeId: 386202,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.LEGAL,
    group: "stridecare-cvvc",
  },
  [Classification.MEDICAL_RECORD_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Medical record requests",
    documentLabel: "Medical Record Request",
    folder: null,
    folderMapping: null,
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "External Routing WF",
    classification: Classification.MEDICAL_RECORD_REQUEST,
    group: "stridecare-cvvc",
  },
  [Classification.ORDER_LAB]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: "provider_staff_bucket",
    displayName: "Lab orders",
    documentLabel: "Care Plan Oversight",
    documentTypeId: 394714,
    folder: "Physician Authorization",
    folderMapping: "Physician Authorization",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.ORDER_LAB,
    group: "stridecare-cvvc",
  },
  // Sheet uses "Care Plan Oversight" here (not the "{{ imagingType }}" label StrideCare uses for
  // this classification) - CVVC's own value, not a transcription error; flagged in the PR.
  [Classification.ORDER_IMAGING]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: "provider_staff_bucket",
    displayName: "Imaging orders",
    documentLabel: "Care Plan Oversight",
    documentTypeId: 394714,
    folder: "Physician Authorization",
    folderMapping: "Physician Authorization",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.ORDER_IMAGING,
    group: "stridecare-cvvc",
  },
  [Classification.CARE_PLAN]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: "provider_staff_bucket",
    displayName: "Plan of care",
    documentLabel: "Care Plan Oversight",
    documentTypeId: 394714,
    folder: "Physician Authorization",
    folderMapping: "Physician Authorization",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.CARE_PLAN,
    group: "stridecare-cvvc",
  },
  [Classification.PHARMACY_RELATED]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: "provider_staff_bucket",
    displayName: "Any Rx document",
    documentLabel: "",
    folder: "Clinical Document",
    folderMapping: "Clinical Document",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.PHARMACY_RELATED,
    group: "stridecare-cvvc",
  },
  [Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: "provider_staff_bucket",
    displayName: "Prior auth approval/denials medications",
    documentLabel: "Insurance {{ authorizationStatus }} - Medication",
    folder: "Clinical Document",
    folderMapping: "Clinical Document",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.PRIOR_AUTHORIZATION_MEDICATION_RESPONSE,
    group: "stridecare-cvvc",
  },
  [Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: "cvneedsubmission",
    displayName: "Prior auth approval/denials for procedures",
    documentLabel: "Insurance {{ authorizationStatus }} - Procedure",
    folder: "Clinical Document",
    folderMapping: "Clinical Document",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.PRIOR_AUTHORIZATION_PROCEDURE_RESPONSE,
    group: "stridecare-cvvc",
  },
  [Classification.PRIOR_AUTHORIZATION_IMAGING_RESPONSE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: "provider_staff_bucket",
    displayName: "Prior auth approval/denials for imaging",
    documentLabel: "Insurance {{ authorizationStatus }} - Imaging",
    folder: "Lab/Imaging",
    folderMapping: "Lab/Imaging",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.PRIOR_AUTHORIZATION_IMAGING_RESPONSE,
    group: "stridecare-cvvc",
  },
  [Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: "cvneedsappeal",
    displayName: "Prior auth request for more information",
    documentLabel: "Request for additional information",
    documentTypeId: 390522,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.PRIOR_AUTHORIZATION_PROCEDURE_REQUEST,
    group: "stridecare-cvvc",
  },
  [Classification.PROGRESS_NOTE_OPERATIVE_REPORT]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: "provider_staff_bucket",
    displayName: "Operative or procedural reports",
    documentLabel: "Operative Report",
    documentTypeId: 342771,
    folder: "Clinic Document - Operative Report",
    folderMapping: "Clinic Document - Operative Report",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.PROGRESS_NOTE_OPERATIVE_REPORT,
    group: "stridecare-cvvc",
  },
  [Classification.PROGRESS_NOTE]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["Close Document"],
    assignedTo: null,
    displayName: "Any progress note/medical history/etc",
    documentLabel: "Historical Medical Record",
    folder: "Medical Record Document - Historical Medical Record",
    folderMapping: "Medical Record Document - Historical Medical Record",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.PROGRESS_NOTE,
    group: "stridecare-cvvc",
  },
  [Classification.INSURANCE_CARD]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: ["Close Document"],
    assignedTo: null,
    displayName: "Insurance fax back request (verification of benefits information)",
    documentLabel: "Explanation of Benefits",
    documentTypeId: 390183,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.INSURANCE_CARD,
    group: "stridecare-cvvc",
  },
  // Email-only, no Athena upload (folder/assignee null); recipient (priscilla.hall@stridecare.com)
  // is routing.ts territory - phase 5.
  [Classification.MEDICAL_RECORD_REQUEST_HEDIS]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "HEDIS & Risk Management",
    documentLabel: "Valerie Health Document",
    folder: null,
    folderMapping: null,
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "External Routing WF",
    classification: Classification.MEDICAL_RECORD_REQUEST_HEDIS,
    group: "stridecare-cvvc",
  },
  [Classification.REFERRAL_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: "provider_staff_bucket",
    displayName: "Requesting a referral from StrideCare CVVC",
    documentLabel: "Request for Additional Information",
    documentTypeId: 390522,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.REFERRAL_REQUEST,
    group: "stridecare-cvvc",
  },
  [Classification.REFERRAL_STATUS_REQUEST]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    assignmentStrategy: "provider_staff_bucket",
    displayName: "Update on referral StrideCare CVVC sent",
    documentLabel: "Referral Acknowledgement",
    documentTypeId: 390844,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Standard",
    classification: Classification.REFERRAL_STATUS_REQUEST,
    group: "stridecare-cvvc",
  },
  [Classification.JUNK]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Junk",
    documentLabel: "Junk",
    folder: null,
    folderMapping: null,
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Delete",
    classification: Classification.JUNK,
    group: "stridecare-cvvc",
  },
  // Rich case-creation workflow (Athena case create + eligibility check + referral email + auth
  // step), matching StrideCare's REFERRAL - not the sheet's literal "Upload + Close the document"
  // (confirmed with Jack; the sheet's separate Referral Mapping tab's business rules - auto-STAT
  // diagnoses, GAE/plantar-fasciitis provider routing, HMO auth, required-fields fax-back - only
  // make sense under this workflow). Rule details land in workflow-steps.ts (phase 6).
  [Classification.REFERRAL]: {
    accountFirstName: null,
    accountId: null,
    accountLastName: null,
    additionalWorkflow: null,
    assignedTo: null,
    displayName: "Referral",
    documentLabel: "New Patient Referral",
    documentTypeId: 391737,
    folder: "Admin",
    folderMapping: "Admin",
    inputDate: "",
    instructions: null,
    workflowAction: null,
    workflowType: "Inbound Referral WF",
    classification: Classification.REFERRAL,
    group: "stridecare-cvvc",
  },
} as const;
